import { useEffect, type FunctionComponent } from 'react';
import classes from './ProviderSearchForm.module.css';
import { useForm } from '@mantine/form';
import { useRouter } from 'next/router';
import PractitionerAutocomplete from './PractitionerAutocomplete';
import { Button } from '@mantine/core';
import LocationAutocomplete from './LocationAutocomplete';
import useQueryString from '@/hooks/useQueryString';
import {
  useProviderSearchInputQueryParams,
  encodeQueryParams,
} from '@/hooks/ProviderSearch/useProviderSearchInputQueryParams';
import useAuthenticatedQuery from '@/hooks/useAuthenticatedQuery';
import { useSpecialtyQuery, useUserAddressQuery } from '@/lib/graphql';
import Icon from '@/components/Icon';
import { ProviderSearchValues } from './types';

export const POSTAL_CODE_PATTERN = /^\d{5}(?:[-\s]\d{4})?$/;

export interface ProviderSearchFormProps {}

const ProviderSearchForm: FunctionComponent<ProviderSearchFormProps> = () => {
  const { push } = useRouter();

  const commonParams = useProviderSearchInputQueryParams();

  const form = useForm<ProviderSearchValues>({
    initialValues: {
      query: {
        text: commonParams.query ?? '',
        matchId: commonParams.matchId ?? undefined,
        matchType: commonParams.matchType ?? undefined,
      },
      location: {
        text: commonParams.location ?? '',
        latitude: commonParams.latitude ?? undefined,
        longitude: commonParams.longitude ?? undefined,
      },
    },
    validate: {
      query: (value) => {
        if (!value.text) {
          return 'A selected specialty or provider is required';
        }
        return null;
      },
      location: (value) => {
        const { text, latitude, longitude } = value;

        if (!text) {
          return 'A city or zip code is required';
        }

        // If we have a lat/lon, then the text is just a label.
        // Otherwise, we need it to be a PostalCode for use in the query.
        if (!latitude || !longitude) {
          if (!POSTAL_CODE_PATTERN.test(text)) {
            return 'Please enter a valid US zip code';
          }
        }
        return null;
      },
    },
  });

  // When populated with a specialty UUID from a Touchpoint task, look up the specialty and set query value:
  const { specialty: specialtyUuid } = useQueryString('specialty');
  useSpecialtyQuery({
    variables: { specialtyId: specialtyUuid },
    skip: !specialtyUuid,
    onCompleted: (data) => {
      if (data.specialty) {
        form.setValues({
          query: {
            text: data.specialty.name,
            matchId: data.specialty.id,
            matchType: 'specialty',
          },
        });
      }
    },
  });

  // When logged in, look up the user's address and autofill the location value to the postal code:
  useAuthenticatedQuery(useUserAddressQuery, {
    onCompleted: (data) => {
      if (data.whoAmI.addresses?.length) {
        const [{ postalCode }] = data.whoAmI.addresses;
        form.setValues({
          location: {
            text: postalCode,
          },
        });
      }
    },
  });

  // If selecting a specific practitioner from the PractitionerAutocomplete, immediately direct to that result.
  useEffect(() => {
    if (
      form.values.query.matchType == 'practitioner' &&
      form.values.query.matchId
    ) {
      push({
        pathname: '/provider-search/info',
        query: { id: form.values.query.matchId },
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps
       -- exclude router since it causes a loop. It is not needed as a dependency of this effect. */
  }, [form.values.query.matchType, form.values.query.matchId]);

  const handleSubmit = (values: ProviderSearchValues) => {
    const queryParams = encodeQueryParams(values);
    push({
      pathname: '/provider-search/results',
      query: queryParams,
    });
  };

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      className={classes.root}
      role="search">
      <PractitionerAutocomplete
        autoFocus
        role="searchbox"
        leftSection={<Icon name="feather/search" size="sm" meaning="neutral" />}
        label="Search by Specialty or Provider"
        {...form.getInputProps('query')}
      />

      <LocationAutocomplete
        role="searchbox"
        leftSection={
          <Icon name="feather/map-pin" size="sm" meaning="neutral" />
        }
        label="City or Zip Code"
        {...form.getInputProps('location')}
      />

      <Button type="submit" fullWidth className={classes.submitButton}>
        Search
      </Button>
    </form>
  );
};

export default ProviderSearchForm;
